import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, combineLatest, map, shareReplay, switchMap, of, catchError, startWith } from 'rxjs';

import { TmtLoggerService } from 'tmt-logger';

import { ApolloClientService } from 'src/app/services/apollo-client.service';
import { blogNewsQueries } from 'src/app/graphql/queries/blog-news.queries';
import { blogNewsMutations } from 'src/app/graphql/mutations/blog-news.mutations';
import { BlogPost } from 'src/app/models/blog-post';
import { LaunchDarklyService } from 'src/app/services/launchdarkly.service';

@Injectable()
export class BlogNewsService {
	constructor(
		private apolloClientService: ApolloClientService,
		private http: HttpClient,
		private loggerService: TmtLoggerService,
		private launchDarklyService: LaunchDarklyService,
	) {}

	private canEdit$ = this.launchDarklyService.launchDarklyFlags$.pipe(map(flags => !!flags['blogs-news-edit']));

	public blogPosts$: Observable<BlogPost[]> = combineLatest([
		this.apolloClientService.abstractionLayerClient$.pipe(
			switchMap(client =>
				this.apolloClientService
					.observableWatchQuery$(client, {
						query: blogNewsQueries.blogNewsAll,
						errorPolicy: 'all',
					})
					.pipe(
						switchMap(response => {
							const presignedUrl = response.data?.blogNewsAll;
							if (typeof presignedUrl !== 'string' || !presignedUrl.trim()) {
								return of([] as BlogPost[]);
							}
							return this.http.get<unknown[]>(presignedUrl).pipe(
								map(raw =>
									(Array.isArray(raw) ? raw : []).map(item =>
										this.normalizeBlogPostItem(item as Record<string, unknown>),
									),
								),
								catchError(err => {
									this.loggerService.logDebug('blogNewsAll: failed to fetch JSON from presigned URL', err);
									return of([] as BlogPost[]);
								}),
							);
						}),
					),
			),
		),
		this.canEdit$,
	]).pipe(
		map(([posts, canEdit]) => canEdit ? posts : posts.filter(post => post.accepted === true && !post.archived)),
		map(posts =>
			[...posts].sort((a, b) => {
				const aTime = parseInt(a.createdAt ?? '0');
				const bTime = parseInt(b.createdAt ?? '0');
				return bTime - aTime; // newest first
			}),
		),
		shareReplay(1),
	);

	/** First emission is loading with empty posts; then posts when GraphQL + presigned JSON fetch complete. */
	public blogPostsView$: Observable<{ loading: boolean; posts: BlogPost[] }> = this.blogPosts$.pipe(
		map(posts => ({ loading: false, posts })),
		startWith({ loading: true, posts: [] as BlogPost[] }),
		shareReplay(1),
	);

	private normalizeBlogPostItem(item: Record<string, unknown>): BlogPost {
		const { __typename, ...blogPost } = item as Record<string, unknown> & { __typename?: string };
		let normalizedLabels: string[] = [];
		if (Array.isArray(blogPost['issueLabels'])) {
			normalizedLabels = blogPost['issueLabels'] as string[];
		} else if (typeof blogPost['issueLabels'] === 'string') {
			const raw = blogPost['issueLabels'] as string;
			if (raw.trim().startsWith('[')) {
				try {
					const parsed = JSON.parse(raw);
					if (Array.isArray(parsed)) {
						normalizedLabels = parsed.map((v: unknown) => String(v).trim()).filter(v => v.length > 0);
					}
				} catch {
					// fall back to comma split below
				}
			}
			if (normalizedLabels.length === 0) {
				normalizedLabels = raw
					.split(',')
					.map(label => label.trim())
					.filter(label => label.length > 0);
			}
		}
		return {
			...blogPost,
			post: typeof blogPost['post'] === 'string' ? blogPost['post'] : '',
			issueLabels: normalizedLabels,
		} as BlogPost;
	}

	public updateBlogPost$(
		original: BlogPost,
		updatedPost: string,
		accepted: boolean,
		issueLabels: string,
		archived: boolean = false,
		issueKey: string = '',
	) {
		this.loggerService.logDebug('Updating blog/news post');
		return this.apolloClientService.abstractionLayerClient$.pipe(
			switchMap(client =>
				client.mutate({
					mutation: blogNewsMutations.blogNewsUpdatePost,
					variables: {
						postId: original.postId,
						post: updatedPost,
						accepted: accepted,
						issueLabels: issueLabels,
						archived: archived,
						issueKey: issueKey ?? '',
					},
					refetchQueries: [
						{ query: blogNewsQueries.blogNewsAll },
					],
				}),
			),
			map((response: any) => response?.data?.blogNewsUpdatePost as string),
		);
	}
} 